import {
  useWt,
  WuCard,
  WuCardHeader,
  WuHeading,
  WuRadioGroup,
} from '@npm-questionpro/wick-ui-lib'
import {useEffect, useMemo, useState} from 'react'
import {useParams} from 'react-router'
import {dialogApi} from '../../../../../api/dialogApi'
import {DIALOG} from '../../../../../Translation/English/dialog'
import {
  IDialogDetails,
  IResponseStat,
  ITopInteractions,
} from '../../../../../types'
import {ResponseMetric} from './ResponseMetric/ResponseMetric'
import styles from './ResponsesCard.module.scss'

// Stable reference for the `useHighlightedResponses` default — a fresh `[]`
// literal in the destructuring default is a new reference on every render
// while `data` is undefined (i.e. every render before the query resolves),
// which fed straight into the effect below's dependency array and caused
// an infinite render loop (setResponses(highlights) → new state reference →
// re-render → new `[]` default → effect fires again → ...).
const EMPTY_RESPONSES: IResponseStat[] = []

export const ResponsesCard: React.FC = () => {
  const wt = useWt()
  const {dialogId} = useParams<{dialogId: string}>()
  const {data: dialogDetails} = dialogApi.useDialogDetails(dialogId ?? '') as {
    data: IDialogDetails
  }
  const {data: highlights = EMPTY_RESPONSES} =
    dialogApi.useHighlightedResponses(dialogId ?? '') as {data: IResponseStat[]}
  const {data: topInteractions} = dialogApi.useTopInteractions(
    dialogId ?? '',
  ) as {data: ITopInteractions | undefined}

  const [radioOption, setRadioOption] = useState('highlighted')
  const [responses, setResponses] = useState<IResponseStat[]>([])

  const radioOptions = useMemo(
    () => [
      ...(dialogDetails?.enableLikes
        ? [{value: 'mostLiked', label: wt(DIALOG.mostLiked)}]
        : []),
      ...(dialogDetails?.enableDislikes
        ? [{value: 'mostDisliked', label: wt(DIALOG.mostDisliked)}]
        : []),
      ...(dialogDetails?.enableResponse
        ? [{value: 'mostCommented', label: wt(DIALOG.mostCommented)}]
        : []),
      {value: 'highlighted', label: wt(DIALOG.highlighted)},
    ],
    [
      dialogDetails?.enableLikes,
      dialogDetails?.enableDislikes,
      dialogDetails?.enableResponse,
      wt,
    ],
  )

  const defaultRadioOption = radioOptions[0]?.value || 'highlighted'

  useEffect(() => {
    setRadioOption(defaultRadioOption)
  }, [defaultRadioOption])

  useEffect(() => {
    if (radioOption === 'highlighted') {
      setResponses(highlights)
    } else if (topInteractions)
      setResponses(topInteractions[radioOption as keyof ITopInteractions] || [])
  }, [radioOption, highlights, topInteractions])

  useEffect(() => {
    const isCurrentOptionAvailable = radioOptions.some(
      option => option.value === radioOption,
    )

    if (!isCurrentOptionAvailable) {
      setRadioOption(defaultRadioOption)
    }
  }, [radioOption, radioOptions, defaultRadioOption])

  return (
    <>
      <WuCard rounded>
        <WuCardHeader className={styles.cardHeading}>
          <WuHeading size="sm">{wt(DIALOG.topResponses)}</WuHeading>
        </WuCardHeader>
        <div className={styles.responsesAnalytics}>
          <WuRadioGroup
            orientation="horizontal"
            key={defaultRadioOption}
            className={styles.responsesAnalyticsRadiogroup}
            defaultValue={defaultRadioOption}
            dir="ltr"
            onChange={value => {
              setRadioOption(value)
            }}
            options={radioOptions}
          />
        </div>
        <ResponseMetric data={responses} filterType={radioOption} />
      </WuCard>
    </>
  )
}
